import { useMemo } from "react";

import { X } from "lucide-react";

import { Button } from "@/modern/components/ui/button-shadcn";

import { useFileDetailModal } from "../../context/FileDetailModalContext";
import { ExtractionOperationList } from "./ExtractionOperationList";
import { ExtractionResultsView } from "./ExtractionResultsView";
import { DateExtractionResults } from "./results/DateExtractionResults";
import { SummaryExtractionResults } from "./results/SummaryExtractionResults";
import { ChecklistExtractionResults } from "./results/ChecklistExtractionResults";
import { LocationExtractionResults } from "./results/LocationExtractionResults";
import { BudgetExtractionResults } from "./results/BudgetExtractionResults";
import { ClientExtractionResults } from "./results/ClientExtractionResults";
import { NotesExtractionResults } from "./results/NotesExtractionResults";
import {
  ExtractionType,
  ExtractionResults,
  ExtractDatesResponse,
  ExtractSummaryResponse,
  ExtractChecklistResponse,
  ExtractLocationResponse,
  ExtractBudgetResponse,
  ExtractClientResponse,
  ExtractNotesResponse,
} from "./types";

/**
 * Computes the number of result items based on the extraction type and response data.
 */
const getResultCount = (type: ExtractionType | null, results: ExtractionResults | null): number | undefined => {
  if (!type || !results) return undefined;

  switch (type) {
    case "dates":
      return (results as ExtractDatesResponse).date_cards?.length;
    case "summary":
      return results ? 1 : 0;
    case "checklist":
      return (results as ExtractChecklistResponse).checklists?.length;
    case "location":
      return (results as ExtractLocationResponse).locations?.length;
    case "budget":
      return (results as ExtractBudgetResponse).budgets?.length;
    case "client":
      return (results as ExtractClientResponse).clients?.length;
    case "notes":
      return (results as ExtractNotesResponse).notes?.length;
    default:
      return undefined;
  }
};

export const ExtractionPanel = () => {
  const {
    activeExtraction,
    extractionStates,
    handleRunExtraction,
    handleRunAllExtractions,
    handleViewExtraction,
    handleCloseExtractionPanel,
    handleBackToOperationList,
  } = useFileDetailModal();

  // Derive active extraction's state slice for the results view
  const activeState = activeExtraction ? extractionStates[activeExtraction] : undefined;
  const activeResults = activeState?.results ?? null;
  const activeLoading = activeState?.loading ?? false;
  const activeError = activeState?.error ?? null;
  const activeLoadingPhase = activeState?.loadingPhase ?? null;

  const resultCount = useMemo(
    () => getResultCount(activeExtraction, activeResults),
    [activeExtraction, activeResults]
  );

  const handleSelectOperation = (type: ExtractionType) => {
    handleViewExtraction(type);
  };

  const handleReExtract = () => {
    if (activeExtraction) {
      handleRunExtraction(activeExtraction);
    }
  };

  // State A: Operation selection list
  if (!activeExtraction) {
    return (
      <div className="flex flex-col h-full min-h-0 bg-card">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
          <span className="text-sm font-medium text-foreground">Document Extraction</span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={handleCloseExtractionPanel}
            aria-label="Close extraction panel"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <ExtractionOperationList
            onSelectOperation={handleSelectOperation}
            onRunAll={handleRunAllExtractions}
            extractionStates={extractionStates}
          />
        </div>
      </div>
    );
  }

  // State B: Results view — derived from extractionStates[activeExtraction]
  const isCached = !activeLoading && !activeError && !!activeResults;

  return (
    <div className="flex flex-col h-full min-h-0 bg-card">
      <ExtractionResultsView
        type={activeExtraction}
        loading={activeLoading}
        error={activeError}
        onBack={handleBackToOperationList}
        onRetry={handleReExtract}
        onReExtract={isCached ? handleReExtract : undefined}
        resultCount={resultCount}
        loadingPhase={activeLoadingPhase}
        previousResults={activeResults}
      >
        {activeExtraction === "dates" && activeResults && (
          <DateExtractionResults
            dateCards={(activeResults as ExtractDatesResponse).date_cards || []}
          />
        )}

        {activeExtraction === "summary" && activeResults && (
          <SummaryExtractionResults
            summary={(activeResults as ExtractSummaryResponse).summary || ""}
          />
        )}

        {activeExtraction === "checklist" && activeResults && (
          <ChecklistExtractionResults
            checklists={(activeResults as ExtractChecklistResponse).checklists || []}
          />
        )}

        {activeExtraction === "location" && activeResults && (
          <LocationExtractionResults
            locations={(activeResults as ExtractLocationResponse).locations || []}
          />
        )}

        {activeExtraction === "budget" && activeResults && (
          <BudgetExtractionResults
            budgets={(activeResults as ExtractBudgetResponse).budgets || []}
          />
        )}

        {activeExtraction === "client" && activeResults && (
          <ClientExtractionResults
            clients={(activeResults as ExtractClientResponse).clients || []}
          />
        )}

        {activeExtraction === "notes" && activeResults && (
          <NotesExtractionResults
            notes={(activeResults as ExtractNotesResponse)?.notes || []}
          />
        )}
      </ExtractionResultsView>
    </div>
  );
};
